import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { GlobalMessageType } from '@spartacus/core'
import { finalize, switchMap, take } from 'rxjs/operators'
import { ShipmentAgreementDetailStatus } from '../../../../../../models/enum/agreement.enum'
import { ICsv } from '../../../../../../models/interface/csv.interface'
import { IHistoryList } from '../../../../../../models/interface/history-list.interface'
import { ISharedPagination } from '../../../../../../models/interface/shared.interface'
import { IShipment } from '../../../../../../models/interface/shipment.interface'
import { DispenserAgreementService } from '../../../../../../services/agreement/dispenser/dispenser-agreement.service'
import { DispenserShipmentService } from '../../../../../../services/agreement/dispenser/dispenser-shipment.service'
import { CsvService } from '../../../../../../services/shared/csv/csv.service'
import { CustomGlobalMessageService } from '../../../../../../services/shared/custom-global-message/custom-global-message.service'
import { SharedService } from '../../../../../../services/shared/shared.service'
import { ICustomSelectOption } from '../../../../../shared/custom-components/custom-select/modals/interface/custom-select.interface'
import { CustomTableColumnType } from '../../../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'

@Component({
    selector: 'app-dispenser-shipment-detail',
    templateUrl: './dispenser-shipment-detail.component.html',
    styleUrls: ['./dispenser-shipment-detail.component.scss'],
    standalone: false
})
export class DispenserShipmentDetailComponent implements OnInit {
  public shipment: IShipment
  public entries: IHistoryList
  public showLoaderOnPagination: boolean
  public showInitialLoader = true
  public selectedPage: number
  public selectedFilter: string
  public selectedSearch: string
  public downloadingCsv = false
  public agreementId: string
  public shipmentId: string
  public creditType = false
  public filterOptions: ICustomSelectOption[] = Object.keys(ShipmentAgreementDetailStatus).map((item) => {
    return {
      label: 'dispenserShipment.status.' + item,
      value: item
    }
  })

  public headers: ICustomTableHeader[] = [
    { title: 'dispenserAgreement.materialNumber' },
    { title: 'dispenserAgreement.materialDescription' },
    { title: 'shared.status' },
    { title: 'dispenserShipment.eta' },
    { title: 'dispenserShipment.quantityIssued' },
    { title: 'dispenserShipment.quantityDispatched' },
    { title: 'dispenserShipment.quantityRemaining' }
  ]

  public columns: ICustomTableColumn[] = [
    { key: 'product.code' },
    { key: 'product.name' },
    { key: 'shipmentEntryStatus' },
    { key: 'namedDeliveryDate', type: CustomTableColumnType.DATE },
    { key: 'quantitySelected' },
    { key: 'quantityDispatched' },
    { key: 'quantityRemaining' }
  ]

  constructor(
    public dispenserAgreementService: DispenserAgreementService,
    public dispenserShipmentService: DispenserShipmentService,
    public globalMessageService: CustomGlobalMessageService,
    public sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    public route: ActivatedRoute,
    protected csvService: CsvService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        switchMap((params) => {
          this.agreementId = params.get('agreementId')
          this.shipmentId = params.get('shipmentId')
          return this.dispenserShipmentService.getShipmentById(this.agreementId, { shipmentId: this.shipmentId })
        }),
        switchMap((detail) => {
          this.shipment = detail

          if (this.shipment?.type === 'CREDIT_NOTE') {
            this.headers.push({ title: 'dispenserShipment.netValue' })
            this.columns.push({ key: 'netValue.formattedValue' })

            this.headers.push({ title: 'dispenserAgreement.invoiceNumber' })
            this.columns.push({ key: 'invoiceNumber' })
          }

          this.changeDetectorRef.detectChanges()
          return this.dispenserShipmentService.getShipmentEntries(this.agreementId, this.shipmentId)
        }),
        take(1)
      )
      .subscribe((entries) => {
        this.entries = entries
        this.entries.results = this.changeEntriesStatus(entries.results)
        this.showInitialLoader = false
        this.changeDetectorRef.detectChanges()
      })
  }

  public pageChange(page: number): void {
    this.selectedPage = page
    this.changePageData({
      pageNumber: this.selectedPage,
      status: this.selectedFilter,
      shipmentEntryId: this.selectedSearch
    })
  }

  public changePageData(params: ISharedPagination): void {
    this.showLoaderOnPagination = true
    this.dispenserShipmentService
      .getShipmentEntries(this.agreementId, this.shipmentId, params)
      .pipe(
        finalize(() => {
          this.showLoaderOnPagination = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe(
        (res) => {
          this.entries = res
          this.entries.results = this.changeEntriesStatus(res.results)
          this.selectedPage = this.entries.pagination.page
        },
        (err) => {
          this.entries = {}
          if (!err.type) {
            this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
          }
        }
      )
  }

  public downloadCSV(): void {
    this.downloadingCsv = true
    this.dispenserShipmentService.downloadCSVEntries(this.agreementId, this.shipmentId).subscribe(
      (results) => {
        let csv: ICsv
        for (const checkCR of results) {
          if (checkCR?.invoiceNumber) {
            this.creditType = true
          }
        }
        if (this.creditType) {
          csv = {
            title: `Shipment Entries`,
            columns: [
              { title: 'dispenserShipment.shipmentLineNumber', key: 'entryNumber' },
              { title: 'dispenserAgreement.materialNumber', key: 'product.code' },
              { title: 'dispenserAgreement.materialDescription', key: 'product.name' },
              { title: 'shared.status', key: 'shipmentEntryStatus' },
              { title: 'dispenserShipment.eta', key: 'namedDeliveryDate', type: CustomTableColumnType.DATE },
              { title: 'dispenserShipment.netValue', key: 'netValue.formattedValue' },
              { title: 'dispenserShipment.quantityIssued', key: 'quantitySelected' },
              { title: 'dispenserShipment.quantityDispatched', key: 'quantityDispatched' },
              { title: 'dispenserShipment.quantityRemaining', key: 'quantityRemaining' },
              { title: 'dispenserAgreement.invoiceNumber', key: 'invoiceNumber' }
            ],
            rows: this.changeEntriesStatus(results)
          }
        } else {
          csv = {
            title: `Shipment Entries`,
            columns: [
              { title: 'dispenserShipment.shipmentLineNumber', key: 'entryNumber' },
              { title: 'dispenserAgreement.materialNumber', key: 'product.code' },
              { title: 'dispenserAgreement.materialDescription', key: 'product.name' },
              { title: 'shared.status', key: 'shipmentEntryStatus' },
              { title: 'dispenserShipment.eta', key: 'namedDeliveryDate', type: CustomTableColumnType.DATE },
              { title: 'dispenserShipment.netValue', key: 'netValue.formattedValue' },
              { title: 'dispenserShipment.quantityIssued', key: 'quantitySelected' },
              { title: 'dispenserShipment.quantityDispatched', key: 'quantityDispatched' },
              { title: 'dispenserShipment.quantityRemaining', key: 'quantityRemaining' }
            ],
            rows: this.changeEntriesStatus(results)
          }
        }
        this.downloadingCsv = false
        this.csvService.generateCSV(csv)
        this.changeDetectorRef.detectChanges()
      },
      (err) => {
        this.downloadingCsv = false
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        this.changeDetectorRef.detectChanges()
      }
    )
  }

  public get disableWhenNoDataOrDownloading(): boolean {
    return !this.entries.results?.length || this.downloadingCsv
  }

  public onSearch(value): void {
    this.selectedSearch = value?.trim()
    this.changePageData({
      status: this.selectedFilter,
      sku: this.selectedSearch
    })
  }

  public filterChange(value: string): void {
    this.selectedFilter = value
    this.changePageData({
      status: this.selectedFilter,
      sku: this.selectedSearch
    })
  }

  public get showMessageWhenNoData(): boolean {
    return !this.showInitialLoader && !this.showLoaderOnPagination
  }

  public get showLoaderOnStartupOrWhenNoData(): boolean {
    return this.showInitialLoader || (!this.entries?.results?.length && this.showLoaderOnPagination)
  }

  public changeEntriesStatus(arr: any[]): any[] {
    if (this.shipment?.type === 'CREDIT_NOTE') {
      if (this.shipment.status.toUpperCase() == 'COMPLETED') {
        return arr.map((item) => {
          if (item.shipmentEntryStatus == 'DELIVERED') {
            item.shipmentEntryStatus = 'Completed'
          }
          return item
        })
      }
    }
    return arr
  }
}
